import { tr } from '@/lib/i18n-core';
import { useRef, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { AlertCircle, Flag } from 'lucide-react';
import { cn, timeOnly } from '@/lib/utils';
import { Popover, PopoverAnchor } from '@/components/ui/primitives';
import { AppointmentHoverCard } from './AppointmentHoverCard';
import { chipLook, providerStyle } from './calendar-colors';
import { laneGeometry, type LayoutSlot } from './calendar-layout';
import type { CalendarAppointment, CalendarProvider } from './calendar-types';

const HOVER_OPEN_MS = 350;
const HOVER_CLOSE_MS = 250;

export interface CalendarEventProps {
  appointment: CalendarAppointment;
  provider: CalendarProvider;
  slot: LayoutSlot;
  top: number;
  height: number;
  active: boolean;
  pinned: boolean;
  phone: boolean;
  dragging: boolean;
  settling: boolean;
  reducedMotion: boolean;
  canDrag: boolean;
  onActivate: (id: string | null, pinned: boolean) => void;
  onChanged: () => void;
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

export function CalendarEvent({
  appointment,
  provider,
  slot,
  top,
  height,
  active,
  pinned,
  phone,
  dragging,
  settling,
  reducedMotion,
  canDrag,
  onActivate,
  onChanged,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: CalendarEventProps) {
  const look = chipLook(appointment);
  const geometry = laneGeometry(slot);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };
  const hoverIn = () => {
    if (phone || dragging) return;
    clearTimers();
    if (!active) openTimer.current = setTimeout(() => onActivate(appointment.id, false), HOVER_OPEN_MS);
  };
  const hoverOut = () => {
    if (phone) return;
    clearTimers();
    if (active && !pinned) closeTimer.current = setTimeout(() => onActivate(null, false), HOVER_CLOSE_MS);
  };

  const style: CSSProperties = {
    ...providerStyle(provider.color),
    top,
    height: Math.max(height, 18),
    insetInlineStart: `${geometry.startPct}%`,
    width: `${geometry.widthPct}%`,
    touchAction: canDrag ? 'none' : undefined,
  };

  const chip = (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${appointment.patientName} ${timeOnly(appointment.startsAt)}`}
      data-appointment={appointment.id}
      style={style}
      className={cn(
        'absolute z-10 overflow-hidden px-1.5 py-0.5 text-xs leading-tight focus-ring select-none',
        look.className,
        canDrag && 'cursor-grab',
        dragging && 'cursor-grabbing opacity-40',
        !reducedMotion && 'transition-[top,inset-inline-start,width,height,box-shadow,transform] duration-150 ease-out',
        !reducedMotion && settling && 'scale-[1.03] shadow-md',
        active && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
      )}
      onPointerDown={(event) => {
        clearTimers();
        onPointerDown(event);
      }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onClick={(event) => {
        event.stopPropagation();
        if (dragging) return;
        clearTimers();
        onActivate(active && pinned ? null : appointment.id, true);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate(active ? null : appointment.id, true);
        }
      }}
      onMouseEnter={hoverIn}
      onMouseLeave={hoverOut}
    >
      <div className="flex items-center gap-1">
        {look.pulse && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary motion-safe:animate-pulse" aria-hidden />}
        <span className="truncate font-medium">{appointment.patientName}</span>
        {look.urgent && <Flag className="h-3 w-3 shrink-0 text-destructive" aria-label={tr('opd', 'see_first')} />}
        {look.feeAlert && <AlertCircle className="h-3 w-3 shrink-0 text-warning" aria-label={tr('opd', 'fee_alert')} />}
      </div>
      {height >= 30 && (
        <div className="truncate text-[11px] text-muted-foreground">
          {timeOnly(appointment.startsAt)}
          {appointment.serviceName ? ` · ${appointment.serviceName}` : ''}
          {appointment.tokenNo ? ` · #${appointment.tokenNo}` : ''}
        </div>
      )}
    </div>
  );

  if (phone) return chip;

  return (
    <Popover
      open={active}
      onOpenChange={(open) => {
        if (!open) onActivate(null, false);
      }}
    >
      <PopoverAnchor asChild>{chip}</PopoverAnchor>
      {active && (
        <AppointmentHoverCard
          appointment={appointment}
          provider={provider}
          onClose={() => onActivate(null, false)}
          onChanged={onChanged}
          onPointerEnter={clearTimers}
          onPointerLeave={hoverOut}
          onPin={() => {
            if (!pinned) onActivate(appointment.id, true);
          }}
        />
      )}
    </Popover>
  );
}
